// src/hooks/usePageHeader.ts
import { useEffect } from 'react';
import type { ReactNode } from 'react';
import { usePageHeaderStore } from '@/stores/pageHeaderStore';

/**
 * usePageHeader — registers page title, icon, and breadcrumbs into the global
 * top-bar store.
 *
 * Call once near the top of any page component (after data hooks, before render).
 * The hook sets the header on mount and clears it on unmount.
 *
 * NOTE on breadcrumbs stability: `breadcrumbs` is serialised with JSON.stringify
 * in the dep array to avoid infinite re-render loops when the array is constructed
 * inline. Alternatively, memoize the breadcrumbs array at the call site.
 */
export function usePageHeader(opts: {
  title: string;
  icon?: ReactNode;
  breadcrumbs?: { label: string; path: string }[];
}) {
  const setHeader = usePageHeaderStore((s) => s.setHeader);
  const clearHeader = usePageHeaderStore((s) => s.clearHeader);

  useEffect(() => {
    setHeader(opts);
    return () => clearHeader();
    // breadcrumbs is JSON-stringified to avoid re-render on inline array identity change
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [opts.title, opts.icon, JSON.stringify(opts.breadcrumbs)]);
}
